import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { ChevronLeftIcon, ShoppingBagIcon, HeartIcon, ZoomInIcon, XIcon, MinusIcon, PlusIcon } from 'lucide-react';
import { lookbookCollections } from '../data/lookbookData';
export function LookbookDetail() {
  const {
    id
  } = useParams();
  const {
    addToCart
  } = useCart();
  const collection = lookbookCollections.find(c => c.id === id);
  const [activeImage, setActiveImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isZoomed, setIsZoomed] = useState(false);
  const [activeProduct, setActiveProduct] = useState(0);
  if (!collection) {
    return <div className="bg-white min-h-screen pt-32 pb-20 px-6 text-center" data-id="element-624">
        <h1 className="text-2xl font-display tracking-widest uppercase" data-id="element-625">
          Editorial Not Found
        </h1>
        <Link to="/lookbook" className="inline-block mt-8 text-[10px] font-display tracking-widest uppercase border-b border-black" data-id="element-626">
          Back to Lookbook
        </Link>
      </div>;
  }
  const product = collection.products[activeProduct];
  return <div className="bg-white min-h-screen" data-id="element-627">
      {/* Breadcrumb */}
      <div className="pt-28 px-6 max-w-[1800px] mx-auto" data-id="element-628">
        <div className="flex items-center space-x-3 text-xs font-display font-bold tracking-widest uppercase text-black/40 mb-12" data-id="element-629">
          <Link to="/" className="hover:text-black transition-colors" data-id="element-630">
            Home
          </Link>
          <span data-id="element-631">/</span>
          <Link to="/lookbook" className="hover:text-black transition-colors" data-id="element-632">
            Lookbook
          </Link>
          <span data-id="element-633">/</span>
          <span className="text-black" data-id="element-634">{collection.title}</span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="px-6 max-w-[1800px] mx-auto mb-20" data-id="element-635">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-636">
          {/* Image Gallery */}
          <div className="lg:col-span-7" data-id="element-637">
            <div className="aspect-[4/5] bg-grey-divider overflow-hidden mb-4 relative group" data-id="element-638">
              <AnimatePresence mode="wait" data-id="element-639">
                <motion.img key={activeImage} src={collection.gallery[activeImage]} alt={collection.title} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.4
              }} className="w-full h-full object-cover" data-id="element-640" />
              </AnimatePresence>

              {/* Hotspots */}
              {activeImage === 0 && collection.products.map((p, idx) => <motion.div key={idx} initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} className="absolute z-10 group/hotspot" style={{
              left: `${p.x}%`,
              top: `${p.y}%`
            }} data-id="element-641">
                    <button onClick={e => {
                e.stopPropagation();
                setActiveProduct(idx);
              }} className="relative w-6 h-6 flex items-center justify-center" data-id="element-642">
                      <span className="absolute inset-0 bg-white rounded-full animate-ping opacity-40" data-id="element-643" />
                      <span className={`relative w-3 h-3 rounded-full border border-white shadow-xl transition-all duration-300 ${activeProduct === idx ? 'bg-black scale-125' : 'bg-white/80'}`} data-id="element-644" />
                    </button>

                    <div className="absolute left-8 top-1/2 -translate-y-1/2 bg-white p-4 shadow-2xl opacity-0 invisible group-hover/hotspot:opacity-100 group-hover/hotspot:visible transition-all duration-300 w-48 border border-grey-divider pointer-events-none group-hover/hotspot:pointer-events-auto" data-id="element-645">
                      <p className="text-xs font-display font-bold tracking-widest uppercase mb-1" data-id="element-646">
                        {p.name}
                      </p>
                      <p className="text-sm font-light mb-3" data-id="element-647">{p.price}</p>
                      <button onClick={() => setActiveProduct(idx)} className="w-full bg-black text-white py-2 text-xs font-display uppercase tracking-widest flex items-center justify-center space-x-2" data-id="element-648">
                        <ShoppingBagIcon className="w-3 h-3" data-id="element-649" />
                        <span data-id="element-650">View Details</span>
                      </button>
                    </div>
                  </motion.div>)}

              <button onClick={() => setIsZoomed(true)} className="absolute top-4 right-4 bg-white/80 backdrop-blur-sm p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-20" data-id="element-651">
                <ZoomInIcon className="w-4 h-4" data-id="element-652" />
              </button>
            </div>

            {/* Thumbnail Strip */}
            <div className="grid grid-cols-4 gap-3" data-id="element-653">
              {collection.gallery.map((img, idx) => <button key={idx} onClick={() => setActiveImage(idx)} className={`aspect-square overflow-hidden relative transition-all ${activeImage === idx ? 'ring-2 ring-black' : 'opacity-60 hover:opacity-100'}`} data-id="element-654">
                  <img src={img} alt={`Pose ${idx + 1}`} className="w-full h-full object-cover" data-id="element-655" />
                </button>)}
            </div>
          </div>

          {/* Product Info */}
          <div className="lg:col-span-5 lg:sticky lg:top-32 h-fit" data-id="element-656">
            <div className="border-l border-grey-divider pl-8 lg:pl-12" data-id="element-657">
              <Link to="/lookbook" className="inline-flex items-center space-x-2 text-xs font-display font-bold tracking-widest uppercase text-black/40 hover:text-black transition-colors mb-8" data-id="element-658">
                <ChevronLeftIcon className="w-3 h-3" data-id="element-659" />
                <span data-id="element-660">Back to Lookbook</span>
              </Link>

              <span className="text-xs md:text-sm font-display font-bold uppercase tracking-[0.4em] mb-4 block text-black/40" data-id="element-661">
                Featuring {collection.model}
              </span>
              <h1 className="text-xl sm:text-3xl md:text-4xl font-display font-bold tracking-[0.05em] md:tracking-[0.2em] uppercase mb-6 leading-tight" data-id="element-662">
                {collection.title}
              </h1>
              <p className="text-sm md:text-base font-normal tracking-wider text-black/70 leading-relaxed mb-12" data-id="element-663">
                {collection.narrative}
              </p>

              {/* Product Variation Selector */}
              {collection.products.length > 1 && <div className="mb-10" data-id="element-664">
                  <h4 className="text-xs font-display font-bold tracking-widest uppercase mb-4 text-black/40" data-id="element-665">
                    Featured Pieces
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-666">
                    {collection.products.map((p, idx) => <button key={idx} onClick={() => {
                  setActiveProduct(idx);
                  setSelectedColor(0);
                  setSelectedSize(null);
                }} className={`px-4 py-2 text-xs font-display tracking-widest uppercase border transition-colors ${activeProduct === idx ? 'border-black bg-black text-white' : 'border-grey-divider hover:border-black'}`} data-id="element-667">
                        {p.name}
                      </button>)}
                  </div>
                </div>}

              {/* Active Product Details */}
              <div className="space-y-8" data-id="element-668">
                <div className="flex justify-between items-end border-b border-grey-divider pb-6" data-id="element-669">
                  <div data-id="element-670">
                    <p className="text-xs font-display font-bold tracking-widest uppercase mb-2 text-black/40" data-id="element-671">
                      Featured Piece
                    </p>
                    <h2 className="text-xl font-display font-bold tracking-widest uppercase" data-id="element-672">
                      {product.name}
                    </h2>
                  </div>
                  <p className="text-xl font-display tracking-widest" data-id="element-673">
                    {product.price}
                  </p>
                </div>

                {/* Color Selector */}
                <div data-id="element-674">
                  <div className="flex justify-between items-baseline mb-4" data-id="element-675">
                    <h4 className="text-xs font-display font-bold tracking-widest uppercase" data-id="element-676">
                      Color
                    </h4>
                    <p className="text-xs font-display tracking-widest uppercase text-black/60" data-id="element-677">
                      {product.colors[selectedColor].name}
                    </p>
                  </div>
                  <div className="flex space-x-3" data-id="element-678">
                    {product.colors.map((color, idx) => <button key={idx} onClick={() => setSelectedColor(idx)} className={`w-10 h-10 rounded-full border-2 transition-all ${selectedColor === idx ? 'border-black scale-110' : 'border-grey-divider hover:border-black/40'}`} style={{
                    backgroundColor: color.hex
                  }} aria-label={color.name} data-id="element-679" />)}
                  </div>
                </div>

                {/* Size Selector */}
                <div data-id="element-680">
                  <div className="flex justify-between items-baseline mb-4" data-id="element-681">
                    <h4 className="text-xs font-display font-bold tracking-widest uppercase" data-id="element-682">
                      Size
                    </h4>
                    <button className="text-xs font-display tracking-widest uppercase text-black/60 border-b border-black/20 hover:border-black" data-id="element-683">
                      Size Guide
                    </button>
                  </div>
                  <div className="grid grid-cols-5 gap-2" data-id="element-684">
                    {product.sizes.map(size => <button key={size} onClick={() => setSelectedSize(size)} className={`py-3 text-xs font-display font-bold tracking-widest border transition-colors ${selectedSize === size ? 'border-black bg-black text-white' : 'border-grey-divider hover:border-black'}`} data-id="element-685">
                        {size}
                      </button>)}
                  </div>
                </div>

                {/* Quantity */}
                <div data-id="element-686">
                  <h4 className="text-xs font-display font-bold tracking-widest uppercase mb-4" data-id="element-687">
                    Quantity
                  </h4>
                  <div className="inline-flex items-center border border-grey-divider" data-id="element-688">
                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-3 hover:bg-black/5 transition-colors" data-id="element-689">
                      <MinusIcon className="w-3 h-3" data-id="element-690" />
                    </button>
                    <span className="px-6 text-xs font-display font-bold tracking-widest" data-id="element-691">
                      {quantity}
                    </span>
                    <button onClick={() => setQuantity(quantity + 1)} className="p-3 hover:bg-black/5 transition-colors" data-id="element-692">
                      <PlusIcon className="w-3 h-3" data-id="element-693" />
                    </button>
                  </div>
                </div>

                {/* CTAs */}
                <div className="space-y-3 pt-4" data-id="element-694">
                  <button onClick={() => {
                  if (selectedSize) {
                    addToCart({
                      id: `${collection.id}-${product.name}`,
                      name: product.name,
                      price: product.price,
                      image: collection.gallery[0],
                      quantity: quantity,
                      size: selectedSize,
                      color: product.colors[selectedColor].name
                    });
                  }
                }} className="w-full bg-black text-white py-4 text-xs font-display font-bold uppercase tracking-widest hover:bg-black/80 transition-colors flex items-center justify-center space-x-3 disabled:opacity-40 disabled:cursor-not-allowed" disabled={!selectedSize} data-id="element-695">
                    <ShoppingBagIcon className="w-4 h-4" data-id="element-696" />
                    <span data-id="element-697">{selectedSize ? 'Add to Bag' : 'Select a Size'}</span>
                  </button>
                  <button className="w-full border border-black py-4 text-xs font-display font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-colors flex items-center justify-center space-x-3" data-id="element-698">
                    <HeartIcon className="w-4 h-4" data-id="element-699" />
                    <span data-id="element-700">Add to Wishlist</span>
                  </button>
                </div>

                {/* Meta */}
                <div className="pt-6 space-y-2 text-xs font-display tracking-widest uppercase text-black/50" data-id="element-701">
                  <p data-id="element-702">Free shipping over $300</p>
                  <p data-id="element-703">Bespoke alterations available in-studio</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial Spread */}
      <section className="px-6 max-w-[1800px] mx-auto mb-24" data-id="element-704">
        <div className="text-center mb-16" data-id="element-705">
          <span className="text-xs font-display uppercase tracking-[0.5em] mb-4 block text-black/40" data-id="element-706">
            The Editorial
          </span>
          <h2 className="text-xl sm:text-2xl md:text-4xl font-display font-bold tracking-[0.2em] uppercase" data-id="element-707">
            {collection.model} in Motion
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-1" data-id="element-708">
          {collection.gallery.map((img, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: idx * 0.1
        }} onClick={() => {
          setActiveImage(idx);
          setIsZoomed(true);
        }} className={`relative overflow-hidden bg-grey-divider cursor-zoom-in ${idx % 3 === 0 ? 'aspect-[4/5]' : 'aspect-[3/4]'}`} data-id="element-709">
              <img src={img} alt={`Editorial pose ${idx + 1}`} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" data-id="element-710" />
              <div className="absolute bottom-6 left-6 text-white text-xs font-display tracking-widest uppercase drop-shadow-md" data-id="element-711">
                Look {String(idx + 1).padStart(2, '0')}
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* You May Also Like */}
      <section className="px-6 max-w-[1800px] mx-auto pb-20 border-t border-grey-divider pt-20" data-id="element-712">
        <h2 className="text-xl sm:text-2xl md:text-4xl font-display font-bold tracking-widest uppercase mb-12 text-center" data-id="element-713">
          You May Also Like
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-714">
          {lookbookCollections.filter(c => c.id !== collection.id).slice(0, 4).map(c => <Link to={`/lookbook/${c.id}`} key={c.id} className="group" data-id="element-715">
                <div className="aspect-[3/4] bg-grey-divider overflow-hidden mb-4" data-id="element-716">
                  <img src={c.image} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-717" />
                </div>
                <h3 className="text-xs md:text-sm font-display font-bold tracking-widest uppercase mb-1" data-id="element-718">
                  {c.title}
                </h3>
                <p className="text-xs font-display tracking-widest uppercase text-black/40" data-id="element-719">
                  {c.model}
                </p>
              </Link>)}
        </div>
      </section>

      {/* Zoom Modal */}
      <AnimatePresence data-id="element-720">
        {isZoomed && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-8" onClick={() => setIsZoomed(false)} data-id="element-721">
            <button onClick={() => setIsZoomed(false)} className="absolute top-6 right-6 p-3 text-white hover:bg-white/10 rounded-full transition-colors" data-id="element-722">
              <XIcon className="w-6 h-6" data-id="element-723" />
            </button>
            <motion.img initial={{
          scale: 0.9
        }} animate={{
          scale: 1
        }} src={collection.gallery[activeImage]} alt={collection.title} className="max-h-full max-w-full object-contain" onClick={e => e.stopPropagation()} data-id="element-724" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}